import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';

import { Layout } from 'antd';

import styled from 'styled-components';

import { addStatementLog } from '@libs/NwLogs';

import { forceReloadProject } from '@hooks/API/Project/UseProject';
import { forceReloadProjectAccountingDetails } from '@hooks/API/Project/UseProjectAccountingDetails';
import { forceReloadSearchInvoices, useInvoice, useInvoicePrints } from '@hooks/API/UseInvoices';
import { forceReloadInvoice } from '@hooks/API/UseInvoices';
import useConfigurations from '@hooks/Configurations/UseConfigurations';

import { Loading } from '@components/Gui/Loading';
import { NwLinkButton } from '@components/Gui/NwButton';
import NwDivider from '@components/Gui/NwDivider';
import EditTextsDrawer from '@components/InvoicesAndCreditNotes/Common/EditTextsDrawer';
import InvoicePageHeader from '@components/InvoicesAndCreditNotes/InvoicePage/InvoicePageHeader';
import InvoicePrints from '@components/InvoicesAndCreditNotes/InvoicePage/InvoicePrints';

import SentStatus from '../Common/SentStatus';
import InvoicePageRows from './InvoicePageRows';
import InvoicePageSider from './InvoicePageSider';
import InvoicePageTop from './InvoicePageTop';
import InvoicePageTotals from './InvoicePageTotals';
import InvoiceWithCreditNotes from './InvoiceWithCreditNote';
import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const StyledLayout = styled(Layout)`
    min-height: ${(props) => (props.$inDrawer ? '0' : 'calc(100vh - 220px)')};
    padding: ${(props) => (props.$inDrawer ? '0' : '8px 16px 16px')};
    background: white;
    margin: 0 1rem;
    display: flex;
    flex-direction: row;
`;

const InvoiceContent = styled.div`
    //width: 100%;
    padding: 1rem 2rem;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    flex: 1 1 0;
`;

const InvoicePage = ({ invoiceId, inDrawer, onClose, onShowCreditNoteDrawer }) => {
    const navigate = useNavigate();
    const { data: invoiceData, error } = useInvoice(invoiceId);
    const { data: invoicePrints, error: invoicePrintsError } = useInvoicePrints(invoiceId);
    const configurations = useConfigurations();

    const [showEditDrawer, setShowEditDrawer] = useState(false);
    const [isLocked, setIsLocked] = useState(false);

    const accounts = [];
    const schemas = [];
    if (configurations && configurations.COA) {
        const COAjs = JSON.parse(configurations.COA);
        if (COAjs.accounts && COAjs.accounts.length > 0) {
            accounts.push(...COAjs.accounts);
        }
        if (COAjs.schemas && COAjs.schemas.length > 0) {
            schemas.push(...COAjs.schemas);
        }
    }

    useEffect(() => {
        if (invoiceData) {
            addStatementLog(invoiceData);
            setIsLocked(invoiceData.Posted || invoiceData.ReadyToPost);
        }
    }, [invoiceData]);

    const handleTransactionCreatedOrUpdated = (ID) => {
        forceReloadInvoice(invoiceData.ID);
        forceReloadProjectAccountingDetails(ID);
        forceReloadProject(ID);
    };

    const handleDeleteInvoice = () => {
        if (inDrawer) {
            onClose();
        } else {
            forceReloadSearchInvoices();
            navigate(`/accounting-invoices`);
        }
    };

    const handleCloseTextsDrawer = () => {
        forceReloadInvoice(invoiceData.ID);
        setShowEditDrawer(false);
    };

    return invoiceData ? (
        <div>
            <InvoicePageHeader
                invoiceData={invoiceData}
                inDrawer={inDrawer}
                handleShowEditDrawer={() => setShowEditDrawer(true)}
                onDelete={handleDeleteInvoice}
                locked={isLocked}
                onShowCreditNoteDrawer={onShowCreditNoteDrawer}
            />
            {invoicePrints && invoicePrints.length > 0 && (
                <InvoicePrints invoicePrints={invoicePrints} invoiceId={invoiceId} />
            )}
            <StyledLayout $inDrawer={inDrawer}>
                <InvoicePageSider
                    invoiceData={invoiceData}
                    onShowEditDrawer={() => setShowEditDrawer(true)}
                    locked={isLocked}
                />
                <InvoiceContent>
                    {invoiceData.CreditNoteIDs && invoiceData.CreditNoteIDs.length > 0 && (
                        <InvoiceWithCreditNotes
                            CreditNoteIDs={invoiceData.CreditNoteIDs}
                            onShowCreditNoteDrawer={onShowCreditNoteDrawer}
                        />
                    )}
                    {(invoiceData.ReadyToPost || invoiceData.Posted) && <SentStatus data={invoiceData} />}
                    <InvoicePageTop invoiceData={invoiceData} locked={isLocked} />
                    <NwDivider>INVOICE DESCRIPTION</NwDivider>
                    <div>
                        <p>{invoiceData.HeaderNote}</p>
                        <NwLinkButton
                            //disabled={isLocked}
                            onClick={() => setShowEditDrawer(true)}
                            icon={byPrefixAndName.fasl['message-lines']}
                            label={`${invoiceData.HeaderNote ? 'edit' : 'add'} an invoice description`}
                        />
                    </div>
                    <NwDivider>INVOICE LINES</NwDivider>
                    <InvoicePageRows
                        invoiceData={invoiceData}
                        onTransactionsUpdated={handleTransactionCreatedOrUpdated}
                        locked={isLocked}
                    />
                    <InvoicePageTotals invoiceData={invoiceData} />
                    <NwDivider>INVOICE FOOTER</NwDivider>
                    <div>
                        <p>{invoiceData.FooterNote}</p>
                        <NwLinkButton
                            //disabled={isLocked}
                            onClick={() => setShowEditDrawer(true)}
                            icon={byPrefixAndName.fasl['message-lines']}
                            label={`${invoiceData.FooterNote ? 'edit' : 'add'} a terms additional text`}
                        />
                    </div>
                </InvoiceContent>
            </StyledLayout>
            {showEditDrawer && <EditTextsDrawer onClose={handleCloseTextsDrawer} data={invoiceData} />}
        </div>
    ) : (
        <Loading />
    );
};

export default InvoicePage;
